import { useEffect, useRef, useState } from "react";
import { Reveal } from "./Reveal";
import { cn } from "../../lib/utils";

/** Three disciplines placed on one closed system. */
const NODES = [
  { id: "software", label: "Software", sub: "Development", x: 230, y: 62 },
  { id: "delivery", label: "Delivery", sub: "Deployment", x: 364, y: 292 },
  { id: "cloud", label: "Cloud", sub: "Services", x: 96, y: 292 },
] as const;

/** The relationships between them — the actual subject of this section. */
const LINKS = [
  {
    id: "sw-del",
    from: 0,
    to: 1,
    d: "M 230 62 Q 349 146 364 292",
    label: "Software → Delivery",
    heading: "Architecture decides how software ships.",
    body: "Boundaries, dependencies and state are delivery decisions before they are code decisions. How a system is structured determines how small, safe and repeatable each release can be.",
    notes: ["Service boundaries", "Build and release shape", "Change surface"],
  },
  {
    id: "del-cloud",
    from: 1,
    to: 2,
    d: "M 364 292 Q 230 356 96 292",
    label: "Delivery → Cloud",
    heading: "Delivery defines what infrastructure must do.",
    body: "Pipelines, environments and release strategy set the requirements the platform has to meet. Infrastructure is designed against those requirements rather than assembled after the fact.",
    notes: ["Environment parity", "Provisioning as code", "Release topology"],
  },
  {
    id: "cloud-sw",
    from: 2,
    to: 0,
    d: "M 96 292 Q 111 146 230 62",
    label: "Cloud → Software",
    heading: "Infrastructure returns to the design table.",
    body: "Cost, latency, data placement and platform limits are constraints, not afterthoughts. What we learn from the runtime feeds straight back into architecture — closing the loop.",
    notes: ["Cost and latency limits", "Data placement", "Architecture feedback"],
  },
] as const;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

export function Approach() {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);
  const sectionRef = useRef<HTMLElement | null>(null);
  const reduced = useReducedMotion();
  const link = LINKS[active] ?? LINKS[0];

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || !auto || reduced || typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setInterval> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          timer ??= setInterval(() => setActive((i) => (i + 1) % LINKS.length), 5600);
        } else if (timer) {
          clearInterval(timer);
          timer = undefined;
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (timer) clearInterval(timer);
    };
  }, [auto, reduced]);

  const select = (i: number) => {
    setAuto(false);
    setActive(i);
  };

  return (
    <section
      ref={sectionRef}
      id="approach"
      className="scroll-mt-24 relative isolate overflow-hidden border-t border-hairline py-24 sm:py-28 lg:py-36"
    >
      {/* Layered navy depth instead of a flat panel */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 90% at 20% 0%, color-mix(in oklab, var(--primary) 16%, transparent), transparent 60%), radial-gradient(90% 80% at 90% 100%, color-mix(in oklab, var(--accent) 12%, transparent), transparent 65%), linear-gradient(180deg, color-mix(in oklab, var(--primary) 5%, var(--background)), var(--background) 70%)",
        }}
      />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 noise-veil opacity-[0.05]" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px rule-glow" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16">
          <Reveal className="flex items-center gap-4 self-start">
            <span className="h-px w-10 bg-primary" />
            <p className="eyebrow">Approach</p>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="max-w-[18ch] font-display text-[clamp(1.9rem,4.4vw,3.4rem)] leading-[1.04]">
                One system, three
                <br className="hidden sm:block" /> points of contact.
              </h2>
            </Reveal>
            <Reveal delay={110}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground">
                Software, delivery and cloud are not sequential handovers. Each one sets the
                conditions for the next, and what we learn at the far end returns to the
                first decision. We design against that loop from the start.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid gap-12 lg:mt-24 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:gap-20">
          {/* Topology */}
          <Reveal className="relative mx-auto w-full max-w-[460px]">
            <svg viewBox="0 0 460 400" className="h-auto w-full" role="img" aria-label="Diagram: software, delivery and cloud form a closed engineering loop">
              <defs>
                <radialGradient id="bmt-core" cx="50%" cy="50%">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </radialGradient>
              </defs>

              <circle cx="230" cy="215" r="150" fill="url(#bmt-core)" />
              <circle
                cx="230"
                cy="215"
                r="118"
                fill="none"
                stroke="color-mix(in oklab, var(--foreground) 8%, transparent)"
                strokeDasharray="2 8"
              />

              {LINKS.map((l, i) => (
                <g key={l.id}>
                  <path
                    d={l.d}
                    fill="none"
                    stroke="color-mix(in oklab, var(--foreground) 16%, transparent)"
                    strokeWidth="1"
                  />
                  <path
                    d={l.d}
                    fill="none"
                    stroke="var(--primary)"
                    strokeWidth="1.5"
                    pathLength={1}
                    strokeDasharray="1"
                    strokeDashoffset={i === active ? 0 : 1}
                    className="transition-[stroke-dashoffset] duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                  />
                  {i === active && !reduced && (
                    <circle r="3.5" fill="var(--accent)">
                      <animateMotion dur="2.8s" repeatCount="indefinite" path={l.d} />
                      <animate attributeName="opacity" values="0;1;1;0" dur="2.8s" repeatCount="indefinite" />
                    </circle>
                  )}
                </g>
              ))}

              {NODES.map((n, i) => {
                const lit = LINKS[active].from === i || LINKS[active].to === i;
                return (
                  <g key={n.id}>
                    <circle
                      cx={n.x}
                      cy={n.y}
                      r="26"
                      fill="var(--primary)"
                      className={cn("transition-opacity duration-700", lit ? "opacity-[0.16]" : "opacity-0")}
                    />
                    <rect
                      x={n.x - 7}
                      y={n.y - 7}
                      width="14"
                      height="14"
                      transform={`rotate(45 ${n.x} ${n.y})`}
                      fill={lit ? "var(--primary)" : "var(--background)"}
                      stroke={lit ? "var(--primary)" : "color-mix(in oklab, var(--foreground) 30%, transparent)"}
                      strokeWidth="1"
                      className="transition-all duration-700"
                    />
                    <text
                      x={n.x}
                      y={i === 0 ? n.y - 28 : n.y + 42}
                      textAnchor="middle"
                      className="font-display"
                      fontSize="17"
                      fill={lit ? "var(--foreground)" : "color-mix(in oklab, var(--foreground) 55%, transparent)"}
                    >
                      {n.label}
                    </text>
                    <text
                      x={n.x}
                      y={i === 0 ? n.y - 12 : n.y + 58}
                      textAnchor="middle"
                      fontSize="9"
                      letterSpacing="2.4"
                      fill="color-mix(in oklab, var(--foreground) 40%, transparent)"
                    >
                      {n.sub.toUpperCase()}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Reveal>

          {/* Relationship detail */}
          <div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-hairline pb-5">
              {LINKS.map((l, i) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => select(i)}
                  onFocus={() => select(i)}
                  onMouseEnter={() => select(i)}
                  aria-current={i === active}
                  className={cn(
                    "text-[0.65rem] tracked transition-colors duration-500",
                    i === active ? "text-primary" : "text-muted-foreground/70 hover:text-foreground",
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <div key={link.id} className="reveal mt-10" data-visible="true">
              <h3 className="max-w-[20ch] font-display text-[clamp(1.5rem,3vw,2.15rem)] leading-[1.1]">
                {link.heading}
              </h3>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">{link.body}</p>
              <ul className="mt-8 max-w-md">
                {link.notes.map((note) => (
                  <li
                    key={note}
                    className="flex items-center gap-4 border-b border-hairline py-3 text-[0.7rem] tracked text-muted-foreground first:border-t"
                  >
                    <span aria-hidden="true" className="h-px w-5 bg-primary" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Engineering progression — philosophy, not a service list */}
        <div className="mt-20 border-t border-hairline pt-8">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <p className="text-[0.65rem] tracked text-muted-foreground/60">How work moves</p>
            {["Design", "Build", "Release", "Refine"].map((phase, i) => (
              <span key={phase} className="flex items-center gap-6">
                {i > 0 && <span aria-hidden="true" className="h-px w-8 bg-primary/40" />}
                <span className="font-display text-sm tracking-[-0.01em] text-foreground/80">{phase}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
